import { NavLink, Outlet } from 'react-router-dom';
import { SystemRole } from '@fleet/shared';
import { useAuth } from './auth/useAuth';
import { ImpersonationBar, ImpersonationButton } from './auth/Impersonation';
import { Button } from './ui/Button';

export function AppLayout() {
  const auth = useAuth();
  // Entspricht `RoleService.isFleetManager` im Server: Fuhrparkleitung und
  // Administration haben bei Stammdaten dieselben Rechte. Nicht zu verwechseln
  // mit „Manager" — das ist, wer eine Fahrzeuggruppe freigeben darf.
  const isFleetManager = auth.hasRole(SystemRole.ADMIN) || auth.hasRole(SystemRole.FLEET_MANAGER);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <ImpersonationBar />

      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Fuhrpark</h1>
          </div>

          {auth.isSignedIn && (
            <>
              {/*
                Vier Punkte passen neben den Titel. Die Verwaltung steckt hinter
                „Admin" — sie hat eine eigene Ebene, siehe `AdminLayout`.
              */}
              <nav className="flex gap-1 rounded-md bg-slate-100 p-1">
                <Tab to="/" end>
                  Kalender
                </Tab>
                <Tab to="/bookings">Meine Buchungen</Tab>
                {auth.me?.canApproveBookings && <Tab to="/approvals">Freigaben</Tab>}
                {isFleetManager && <Tab to="/admin">Admin</Tab>}
              </nav>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm font-medium">{auth.me?.displayName ?? '…'}</div>
                  {auth.me?.roles.length ? (
                    <div className="text-xs text-slate-500">{auth.me.roles.join(', ')}</div>
                  ) : null}
                </div>

                <ImpersonationButton />

                <Button onClick={() => void auth.signOut()}>Abmelden</Button>
              </div>
            </>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        {!auth.isConfigured ? (
          <NotConfigured />
        ) : !auth.isSignedIn ? (
          <SignIn onSignIn={() => void auth.signIn()} isLoading={auth.isLoading} />
        ) : (
          <Outlet />
        )}
      </main>

      {/*
        Der Datenschutzhinweis gehört an eine Stelle, die immer da ist. Im
        Buchungsdialog wird er bestätigt — nachlesen können muss man ihn auch
        dann, wenn man gerade nichts bucht.
      */}
      {auth.isSignedIn && (
        <footer className="mx-auto max-w-7xl px-6 pb-8 text-xs text-slate-500">
          <NavLink to="/datenschutz" className="hover:text-slate-900 hover:underline">
            Datenschutzhinweise
          </NavLink>
        </footer>
      )}
    </div>
  );
}

function Tab({ to, end, children }: { to: string; end?: boolean; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          'rounded px-3 py-1.5 text-sm transition-colors',
          isActive ? 'bg-white shadow-sm' : 'text-slate-600 hover:text-slate-900',
        ].join(' ')
      }
    >
      {children}
    </NavLink>
  );
}

function SignIn({ onSignIn, isLoading }: { onSignIn: () => void; isLoading: boolean }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
      <h2 className="text-base font-medium">Anmeldung erforderlich</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
        Melde dich mit deinem Firmenkonto an, um Fahrzeuge zu buchen.
      </p>
      <Button variant="primary" onClick={onSignIn} disabled={isLoading} className="mt-6">
        {isLoading ? 'Anmeldung läuft …' : 'Mit Microsoft anmelden'}
      </Button>
    </div>
  );
}

function NotConfigured() {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
      <h2 className="text-sm font-medium text-amber-900">Anmeldung noch nicht eingerichtet</h2>
      <p className="mt-2 text-sm leading-relaxed text-amber-800">
        In der <code className="font-mono">.env</code> stehen noch die Platzhalter für Microsoft
        Entra. Trage dort Tenant-ID sowie die Client-IDs der SPA- und der API-Registrierung ein —
        die nötigen Schritte stehen in <code className="font-mono">docs/entra-setup.md</code>.
      </p>
    </div>
  );
}
